import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setConditionVariablesAC } from "store/actions/condition";
import { setCurrentTableStateAC } from "store/actions/table";
import { Table } from "components/uiComponents/Table";
import { useTableData } from "../model/use-table-data";
import { useSelection } from "../model/use-selection";
import { useFilteredRows } from "../model/use-filtered-rows";
import { useHeaderActions } from "../model/use-header-actions";
import { HeaderTableActions } from "./HeaderTableActions";
import InfoTableActions from "./InfoTableActions";
import styles from "./styles.module.scss";
import tableStyles from "./table-styles.module.scss";
import useHiddenRows from "../model/use-hidden-rows";
import useFooter from "../model/use-footer";
import { resizeViewer } from "utils/builder/IScheduleLogic";
import { getCurrentNamespace, hasExternalWindows } from "utils/crossTabSync";

const DashboardInfoTableV2 = ({
  table,
  withOutActions = false,
  isExternalWindow = false,
  projectData,
  handleChangeSidebar,
  withInverseAllSelection = false,
}) => {
  const dispatch = useDispatch();
  const { columnsInfo, rowsData, selectedItems = [], id } = table;
  const tableRef = useRef();

  const { columnVisibility, settingsColumns } = useHiddenRows();
  const { columns, types, rows } = useTableData({ columnsInfo, rowsData, tableId: id });
  const { handleRowsChange, filteredRows } = useFilteredRows();
  const modelId = projectData?.idToOpen || null;
  const { selectedRows, onRowSelectionChange } = useSelection(selectedItems, id, modelId);
  const { handleSettingsOpen } = useHeaderActions();
  const footer = useFooter(rows, selectedRows, columns);

  const tabsSettingsForTab = useSelector((s) => s.tabsSettings?.[id] || null);
  const currentTableState = useSelector((s) => s.table?.currentTableState || null);
  const translations = useSelector((s) => s.translations || null);
  const viewerTemplateSettings = useSelector((s) => s.viewer?.templateSettings || null);
  const conditionState = useSelector((s) => s.condition || null);

  useEffect(() => {
    dispatch(setConditionVariablesAC(columns, types));
  }, [columns, dispatch, types]);

  const initialRowSelection = useSelector(
    (s) => s.table?.currentTableState?.rowSelection || {}
  );

  const initialState = useMemo(() => {
    return isExternalWindow ? { rowSelection: initialRowSelection } : undefined;
  }, [isExternalWindow, initialRowSelection]);

  const tableKey = useMemo(() => {
    if (!isExternalWindow) return `tab-${id}`;
    const keys = Object.keys(initialRowSelection || {}).sort().join(",");
    return `ext-${id}-${keys}`;
  }, [isExternalWindow, id, initialRowSelection]);

  const ctrlKey = useMemo(() => {
    return modelId ? `table-ctrl:${modelId}:${id}` : `table-ctrl:${id}`;
  }, [modelId, id]);

  const sessionIdRef = useRef();
  if (!sessionIdRef.current) sessionIdRef.current = Math.random().toString(36).slice(2);
  const sessionId = sessionIdRef.current;

  const onRowSelectionChangeProxy = useCallback(
    (selectedRowsData) => {
      // In this app, Table -> useRowSelection calls onRowSelectionChange(selectedRowsArray)
      onRowSelectionChange?.(selectedRowsData);

      // External window: send selection to host via table-ctrl immediately
      if (isExternalWindow && modelId && id) {
        try {
          const selected = Array.isArray(selectedRowsData) ? selectedRowsData : [];
          const elementIds = [];
          selected.forEach((r) => {
            const ids =
              (Array.isArray(r?.lineElements) && r.lineElements) ||
              (Array.isArray(r?.original?.lineElements) && r.original.lineElements) ||
              [];
            ids.forEach((id) => elementIds.push(id));
          });
          const unique = Array.from(new Set(elementIds));

          const msg = {
            v: 1,
            ts: Date.now(),
            rid: Math.random().toString(36).slice(2),
            from: sessionId,
            event: "selection",
            payload: { elementIds: unique },
          };
          localStorage.setItem(ctrlKey, JSON.stringify(msg));
        } catch (err) {}
        return;
      }
    },
    [onRowSelectionChange, rows.length, ctrlKey, sessionId, isExternalWindow, modelId, id]
  );

  // Host window: send selection to external windows via table-ctrl
  useEffect(() => {
    if (isExternalWindow) return;
    
    const currentNs = getCurrentNamespace();
    if (!currentNs || !hasExternalWindows(currentNs)) {
      return;
    }
    
    const elementIds = Array.from(
      new Set(
        (selectedRows || [])
          .flatMap((r) => r?.original?.lineElements ?? [])
          .filter(Boolean)
      )
    );
    const msg = {
      v: 1,
      ts: Date.now(),
      rid: Math.random().toString(36).slice(2),
      from: sessionId,
      event: "selection",
      payload: { elementIds },
    };
    try {
      localStorage.setItem(ctrlKey, JSON.stringify(msg));
    } catch {}
  }, [ctrlKey, sessionId, selectedRows, isExternalWindow]);

  useEffect(() => {
    if (!isExternalWindow || !modelId || !id) return;
    
    try {
      const { getCurrentNamespace } = require('utils/crossTabSync');
      const currentNs = getCurrentNamespace() || modelId;
      const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : `rowSelection:${modelId}`;
      
      const onStorage = (e) => {
        if (e.storageArea !== localStorage || e.key !== rowSelectionKey) return;
        
        try {
          const raw = localStorage.getItem(rowSelectionKey);
          const parsed = raw ? JSON.parse(raw) : {};
          if (parsed && typeof parsed === "object") {
            dispatch(setCurrentTableStateAC({ rowSelection: parsed }));
            const elementIds = [];
            (rows || []).forEach((row, idx) => {
              const rowId = (() => {
                const ids = Array.isArray(row?.lineElements) ? row.lineElements : [];
                if (ids.length) return `le:${ids.join("|")}`;
                return String(idx);
              })();
              if (parsed[rowId]) {
                const lineEls = Array.isArray(row?.lineElements) ? row.lineElements : [];
                lineEls.forEach((id) => elementIds.push(id));
              }
            });
            
            const uniqueElementIds = Array.from(new Set(elementIds));
            const msg = {
              v: 1,
              ts: Date.now(),
              rid: Math.random().toString(36).slice(2),
              from: sessionId,
              event: "selection",
              payload: { elementIds: uniqueElementIds },
            };
            localStorage.setItem(ctrlKey, JSON.stringify(msg));
          }
        } catch (err) {}
      };
      
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    } catch {}
  }, [isExternalWindow, modelId, id, rows, ctrlKey, sessionId, dispatch]);

  const openActiveTabInNewTab = () => {
    let liveRowSelection = currentTableState?.rowSelection || {};
    try {
      const { getCurrentNamespace, registerExternalWindow } = require('utils/crossTabSync');
      const currentNs = getCurrentNamespace() || modelId;
      const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : (modelId ? `rowSelection:${modelId}` : 'rowSelection');
      const raw = localStorage.getItem(rowSelectionKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === "object") {
        liveRowSelection = parsed;
      }
      
      if (currentNs || modelId) {
        const nsToRegister = currentNs || modelId;
        registerExternalWindow(nsToRegister);
      }
    } catch {}
    const payload = {
      version: 1,
      ts: Date.now(),
      activeTab: { ...table },
      bootstrap: {
        tabsSettings: tabsSettingsForTab ? { [id]: tabsSettingsForTab } : null,
        currentTableState: { ...(currentTableState || {}), rowSelection: liveRowSelection },
        translations,
        condition: conditionState,
        project: projectData || null,
        viewerTemplateSettings,
      },
    };
    const key = `activeTab:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    try {
      localStorage.setItem(key, JSON.stringify(payload));
      const width = Math.min(window.screen.availWidth, 1400);
      const height = Math.min(window.screen.availHeight, 900);
      const left = Math.max(0, Math.floor((window.screen.availWidth - width) / 2));
      const top = Math.max(0, Math.floor((window.screen.availHeight - height) / 2));
      const features = [
        "popup=yes", "noopener", "noreferrer", "resizable=yes", "scrollbars=yes",
        "toolbar=no", "menubar=no", "location=no",
        `width=${width}`, `height=${height}`, `left=${left}`, `top=${top}`,
      ].join(",");
      const url = `/table-window?key=${encodeURIComponent(key)}`;
      const windowName = `BS_TABLE_${key}`;
      try { 
        if (typeof window.__bs_hideCurrentTable === "function") {
          window.__bs_hideCurrentTable(); 
        }
      } catch {}
      const win = window.open(url, windowName, features);
      if (win && typeof win.focus === "function") { try { win.focus(); } catch {} }
    } catch (e) {
      console.error("Cannot save activeTab to localStorage", e);
    }
    setTimeout(() => resizeViewer(), 1000);
  };

  const shouldHideActions = withOutActions || isExternalWindow;

  return (
    <Table
      key={tableKey}
      ref={tableRef}
      initialState={initialState}
      columns={columns}
      columnVisibility={columnVisibility}
      data={rows}
      footer={footer}
      tableStyles={tableStyles}
      wrapperInnerClassName={styles.wrapperInner}
      columnsInfo={columnsInfo}
      isExternalWindow={isExternalWindow}
      modelId={modelId}
      ActionsComponent={
        <InfoTableActions
          handleClearSelection={tableRef.current?.handleClearSelection}
          columns={columns}
          selectedItems={selectedItems}
          selectedRows={selectedRows}
          filteredRows={filteredRows}
          rows={rows}
          hideWrapperInner={isExternalWindow}
          table={table}
          openActiveTabInNewTab={openActiveTabInNewTab}
          modelId={modelId}
        />
      }
      HeaderCustomActions={({ column }) =>
        settingsColumns.includes(column.id) ? (
          <HeaderTableActions
            handleChangeSidebar={handleChangeSidebar}
            handleSettingsOpen={handleSettingsOpen}
            column={column}
            isExternalWindow={isExternalWindow}
          />
        ) : null
      }
      onRowSelectionChange={isExternalWindow ? onRowSelectionChangeProxy : onRowSelectionChange}
      onRowsChange={handleRowsChange}
      withInverseAllSelection={withInverseAllSelection}
      withOutActions={shouldHideActions}
      options={{
        enabledInverseAllSelection: withInverseAllSelection,
      }}
    />
  );
};

export default DashboardInfoTableV2;